import {ID} from '@wandb/weave/core';
import {format} from 'date-fns';

import {ThunkResult} from '../../types/redux';
import {save} from '../views/actions';
import * as ViewActionsInternal from '../views/actionsInternal';
import * as ViewApi from '../views/api';
import {View} from '../views/types';
import {
  getProjectReadonlyState,
  newWorkspaceViews,
  showNewView,
} from './actions';
import {viewNameColumnForNamedWorkspace} from './names';
import {
  getNamedWorkspaceId,
  NamedWorkspaceId,
  NW_PUBLISHED_USERNAME,
} from './types';

// TODO: this is out of date, in the future will be `{username}'s workspace {num}`
export const generateUserWorkspaceDisplayName = (
  username: string | undefined,
  views?: View[] // TODO: will be required in the future
) => {
  return (username ?? '') + format(new Date(), ' yyyy MMM dd HH:mm:ss'); // https://date-fns.org/v2.30.0/docs/format
};

/**
 * We are creating a completely new workspace, so we need to
 * create the new published workspace and the draft for the current user
 * @param workspaceStateId
 * @param nwId
 * @returns
 */
export const createNewWorkspaceAndLoadDraft = (
  workspaceStateId: string,
  draftNwId: NamedWorkspaceId,
  displayName: string
): ThunkResult<Promise<void>> => {
  return async (dispatch, getState, client) => {
    const state = getState();

    const workspace = state.workspaces[workspaceStateId];

    // First create the published version - we always start new workspaces
    // with the empty default spec.
    await dispatch(
      createNewWorkspaceView(
        workspaceStateId,
        getNamedWorkspaceId(
          draftNwId.id,
          NW_PUBLISHED_USERNAME,
          draftNwId.relatedId
        ),

        workspace.defaultSpec,
        displayName
      )
    );

    // create the draft version of workspace - note that
    // since the published is just using the defaults, we can
    // use those for these as well rather than explicitly inherit them
    // from the published version
    const draftViewCid = await dispatch(
      createNewWorkspaceView(
        workspaceStateId,
        draftNwId,
        workspace.defaultSpec,
        displayName
      )
    );

    return dispatch(
      loadRecentlyCreatedWorkspace(workspaceStateId, draftNwId, draftViewCid)
    );
  };
};

/**
 * Call this when you are creating a new draft for an existing
 * workspace
 *
 * @param newDraftnwId what the nwId of the new draft should be
 * @param publishedViewCid the view to be copied
 * */
export const createAndLoadDraftForExistingWorkspace = (
  workspaceStateId: string,
  newDraftnwId: NamedWorkspaceId,
  publishedViewCid: string
): ThunkResult<Promise<void>> => {
  return async (dispatch, getState, client) => {
    const state = getState();

    // We need to pull the spec and display name from the published version,
    // so load that from the server.
    const serverID = state.views.views[publishedViewCid].id;
    if (serverID == null) {
      // We might encounter this condition when we are creating both the published
      // and draft at the same time. However, in those scenarios we should be calling
      // createAndLoadPrimaryAndDraft(), so we should never hit this condition.
      throw new Error(
        "Can't create a draft for an unsaved published workspace"
      );
    }
    const result = await ViewApi.loadSingleViewById(client, serverID);

    // Now we can create the new draft workspace based on the published version.
    const draftCid = await dispatch(
      createNewWorkspaceView(
        workspaceStateId,
        newDraftnwId,
        result.spec,
        result.displayName
      )
    );
    return dispatch(
      loadRecentlyCreatedWorkspace(workspaceStateId, newDraftnwId, draftCid)
    );
  };
};

/**
 * Call this when you don't have a published workspace yet, and
 * you want to show the published workspace, not a draft.
 * This will not create a draft version of the workspace
 *
 * @param newDraftnwId what the nwId of the new draft should be
 * @param publishedViewCid the view to be copied
 * */
export const createNewWorkspaceAndLoadPublished = (
  workspaceStateId: string,
  newPublishedNwId: NamedWorkspaceId,
  displayName: string
): ThunkResult<Promise<void>> => {
  return async (dispatch, getState, client) => {
    if (newPublishedNwId.username !== NW_PUBLISHED_USERNAME) {
      throw new Error(
        'can only be creating published workspaces with this method'
      );
    }
    const workspace = getState().workspaces[workspaceStateId];
    const cid = await dispatch(
      createNewWorkspaceView(
        workspaceStateId,
        newPublishedNwId,
        workspace.defaultSpec,
        displayName
      )
    );
    return await dispatch(
      loadRecentlyCreatedWorkspace(workspaceStateId, newPublishedNwId, cid)
    );
  };
};

/**
 * Does all the steps to create the new row for the view table, including updating reducer state and
 * saving it to the server.
 *
 * Note that this doesn't care if the view is a template or a draft - that's up
 * to the caller based on how they pass in information
 *
 * @returns the cid of the view that was created and saved
 */

const createNewWorkspaceView = (
  workspaceStateId: string,
  nwId: NamedWorkspaceId,
  // Why unknown? If we need to interact with the spec, we could always treat it as Record<string, any>,
  // but I figured since we don't need to do anything with it for now, we can just treat is as unknown and try to
  // avoid involving any as a type here.
  spec: unknown,
  displayName: string
): ThunkResult<Promise<string>> => {
  return async (dispatch, getState, client) => {
    const state = getState();
    const workspace = state.workspaces[workspaceStateId];
    const viewer = state.viewer.viewer;

    const {viewType, entityName, projectName} = workspace;

    const readOnly = getProjectReadonlyState(viewer, workspace, state);

    // it'd be good to move this out to a separate "construct a spec" function
    const name = viewNameColumnForNamedWorkspace(nwId);
    const cid = 'cnw-' + ID(12);
    // I would prefer it if this was Saveable view, but loadFinished wants a loadResult type
    const newView: ViewApi.LoadResultType = {
      // note that the cid is never sent to the server, but is used as a local ID. It will
      // change if the user refreshes the page.
      cid,
      type: viewType,
      name,
      displayName,
      description: '',
      project: {
        entityName,
        name: projectName,
        readOnly,
      },
      spec,
      locked: true, // Not sure that is a meaningful value, but keeping since it's been there before
      // The below fields are so that loadFinished can run successfully
      updatedAt: new Date(),
      createdAt: new Date(),
      user: viewer
        ? {
            id: viewer.id,
            name: viewer.name,
            username: viewer.username ?? viewer.email,
            photoUrl: viewer.photoUrl,
            admin: viewer.admin,
          }
        : {
            id: 'ANONYMOUS',
            name: '',
            username: '',
            photoUrl: '',
            admin: false,
          },
      entityName,
    };

    const viewRef = {type: viewType, id: cid};
    // various calls to ensure we have the proper state in reducers to know that this workspace view exists
    // Note that loadFinished must come before save so that the save doesn't fail when it looks for the
    // save saga looks for that viewRef
    await dispatch(ViewActionsInternal.loadFinished(cid, newView, true));
    // tell the server that this new view exists
    await dispatch(save(viewRef));
    await dispatch(newWorkspaceViews(workspaceStateId, [cid]));
    return Promise.resolve(cid);
  };
};

/**
 * For recently created workspaces, we can't assume that the save action has occurred, so we can't
 * request the full view including the spec from the server. But since we know we have
 * the full view with the spec stored in the redux store, we can just call showNewView without needing
 * to invoke the load step.
 *
 * This method assumes that it is being called after createNewWorkspaceView(), but the real dependency is
 * that ViewActionsInternal.loadFinished has been called.
 */
const loadRecentlyCreatedWorkspace = (
  workspaceStateId: string,
  nwId: NamedWorkspaceId,
  viewCid: string
): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const workspace = getState().workspaces[workspaceStateId];
    dispatch(ViewActionsInternal.deleteUndoRedoHistory());
    await dispatch(
      showNewView(
        workspaceStateId,
        {type: workspace.viewType, id: viewCid},
        nwId
      )
    );
  };
};
